import {
  Customer,
  ServiceProvider,
  ServiceCategory,
  ServiceRequest,
  Payment,
  Complaint,
  Review,
  AppNotification,
  CmsPage,
  FaqItem,
  DashboardStats,
} from '../types';

const cities = ['Austin', 'Dallas', 'Houston', 'San Antonio', 'Fort Worth', 'Plano', 'Irving'];
const methods = ['Card', 'Apple Pay', 'Google Pay', 'Bank Transfer'];

const firstNames = [
  'James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda',
  'William', 'Elizabeth', 'David', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Jessica',
  'Thomas', 'Sarah', 'Charles', 'Karen', 'Daniel', 'Nancy', 'Matthew', 'Lisa',
  'Anthony', 'Betty', 'Mark', 'Margaret', 'Donald', 'Sandra',
];

const lastNames = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
  'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson',
  'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson',
  'White', 'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson',
];

const nameAt = (i: number) => `${firstNames[i % firstNames.length]} ${lastNames[i % lastNames.length]}`;
const emailAt = (name: string, i: number) =>
  `${name.toLowerCase().replace(' ', '.')}${i}@email.com`;
const phoneAt = (i: number) => `+1 (555) ${String(100 + (i % 900)).padStart(3, '0')}-${String(1000 + i).slice(-4)}`;
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString();
};

export const mockCustomers: Customer[] = Array.from({ length: 28 }, (_, i) => {
  const name = nameAt(i);
  const statuses: Customer['status'][] = ['Active', 'Active', 'Active', 'Suspended', 'Deactivated'];
  return {
    id: `USR-${String(1001 + i)}`,
    name,
    email: emailAt(name, i),
    phone: phoneAt(i),
    avatar: undefined,
    registrationDate: daysAgo(120 - i * 3),
    status: statuses[i % statuses.length],
    addresses: [
      {
        id: `ADDR-${i}-1`,
        label: 'Home',
        fullAddress: `${100 + i} Oak Street, Apt ${(i % 5) + 1}`,
        city: cities[i % cities.length],
        isDefault: true,
      },
      ...(i % 3 === 0
        ? [
            {
              id: `ADDR-${i}-2`,
              label: 'Office',
              fullAddress: `${200 + i} Commerce Blvd`,
              city: cities[(i + 1) % cities.length],
              isDefault: false,
            },
          ]
        : []),
    ],
  };
});

const categories = [
  'Plumbing',
  'Electrical',
  'Cleaning',
  'Painting',
  'Repairs',
  'AC Maintenance',
  'Carpentry',
  'Moving',
  'Landscaping',
  'Appliance Repair',
];

export const mockProviders: ServiceProvider[] = Array.from({ length: 20 }, (_, i) => {
  const name = nameAt(i + 5);
  const primary = categories[i % categories.length];
  const verifications: ServiceProvider['verificationStatus'][] = [
    'Approved',
    'Approved',
    'Pending',
    'Rejected',
    'Approved',
  ];
  const statuses: ServiceProvider['status'][] = ['Active', 'Active', 'Suspended', 'Active', 'Deactivated'];
  return {
    id: `PRV-${String(2001 + i)}`,
    name,
    email: emailAt(name, i + 50),
    phone: phoneAt(i + 40),
    primaryCategory: primary,
    categories: [primary, categories[(i + 2) % categories.length]],
    skills: [`${primary} Specialist`, 'Customer Service', 'On-site Estimates'],
    experienceYears: 2 + (i % 15),
    serviceArea: `${cities[i % cities.length]} Metro`,
    about: `Experienced ${primary.toLowerCase()} professional serving residential and commercial clients.`,
    address: `${300 + i} Service Lane, ${cities[i % cities.length]}`,
    applicationDate: daysAgo(90 - i * 2),
    verificationStatus: verifications[i % verifications.length],
    status: statuses[i % statuses.length],
    documents: [
      {
        id: `DOC-${i}-1`,
        type: 'Government ID',
        uploadedAt: daysAgo(88 - i * 2),
        status: verifications[i % verifications.length],
      },
      {
        id: `DOC-${i}-2`,
        type: 'Trade License',
        uploadedAt: daysAgo(87 - i * 2),
        status: i % 5 === 2 ? 'Pending' : verifications[i % verifications.length],
        rejectionReason: i % 5 === 3 ? 'Document expired' : undefined,
      },
      {
        id: `DOC-${i}-3`,
        type: 'Insurance Certificate',
        uploadedAt: daysAgo(86 - i * 2),
        status: verifications[i % verifications.length],
      },
    ],
  };
});

export const mockCategories: ServiceCategory[] = categories.map((name, i) => ({
  id: `CAT-${String(301 + i)}`,
  name,
  description: `Professional ${name.toLowerCase()} services for homes and businesses.`,
  icon: name.slice(0, 2).toUpperCase(),
  status: i === 8 ? 'Inactive' : 'Active',
  createdAt: daysAgo(200 - i * 5),
  providerCount: 8 + (i % 10),
  requestCount: 40 + i * 12,
  activeJobs: 3 + (i % 7),
  completedJobs: 30 + i * 8,
  subcategories: [
    {
      id: `SUB-${i}-1`,
      name: `${name} — Residential`,
      description: `Residential ${name.toLowerCase()} work`,
      status: 'Active',
      createdAt: daysAgo(180 - i),
    },
    {
      id: `SUB-${i}-2`,
      name: `${name} — Commercial`,
      description: `Commercial ${name.toLowerCase()} work`,
      status: i % 4 === 0 ? 'Inactive' : 'Active',
      createdAt: daysAgo(170 - i),
    },
    {
      id: `SUB-${i}-3`,
      name: `${name} — Emergency`,
      description: `24/7 emergency ${name.toLowerCase()}`,
      status: 'Active',
      createdAt: daysAgo(160 - i),
    },
  ],
}));

const jobStatuses: ServiceRequest['status'][] = [
  'Pending',
  'Assigned',
  'Accepted',
  'In Progress',
  'Completed',
  'Cancelled',
];
const payStatuses: Payment['status'][] = [
  'Successful',
  'Pending',
  'Failed',
  'Cancelled',
  'Refunded',
];

export const mockRequests: ServiceRequest[] = Array.from({ length: 24 }, (_, i) => {
  const customer = mockCustomers[i % mockCustomers.length];
  const provider = mockProviders[i % mockProviders.length];
  const status = jobStatuses[i % jobStatuses.length];
  const assigned = status !== 'Pending';
  return {
    id: `REQ-${String(4001 + i)}`,
    customerId: customer.id,
    customerName: customer.name,
    category: categories[i % categories.length],
    providerId: assigned ? provider.id : undefined,
    providerName: assigned ? provider.name : undefined,
    description: `Need help with ${categories[i % categories.length].toLowerCase()} — ${
      i % 2 === 0 ? 'urgent residential request' : 'scheduled maintenance visit'
    }.`,
    scheduledDate: daysAgo(-(i % 14)),
    scheduledTime: `${9 + (i % 8)}:00 AM`,
    estimatedPrice: 85 + i * 15,
    address: customer.addresses[0].fullAddress,
    city: customer.addresses[0].city,
    status,
    paymentStatus: payStatuses[i % payStatuses.length],
    createdAt: daysAgo(30 - i),
    timeline: [
      { id: `TL-${i}-1`, label: 'Request Created', date: daysAgo(30 - i) },
      ...(assigned
        ? [
            { id: `TL-${i}-2`, label: 'Provider Assigned', date: daysAgo(29 - i) },
            { id: `TL-${i}-3`, label: 'Accepted', date: daysAgo(28 - i) },
          ]
        : []),
      ...(status === 'In Progress' || status === 'Completed'
        ? [{ id: `TL-${i}-4`, label: 'In Progress', date: daysAgo(27 - i) }]
        : []),
      ...(status === 'Completed'
        ? [{ id: `TL-${i}-5`, label: 'Completed', date: daysAgo(26 - i) }]
        : []),
      ...(status === 'Cancelled'
        ? [{ id: `TL-${i}-6`, label: 'Cancelled', date: daysAgo(25 - i), note: 'Customer cancelled' }]
        : []),
    ],
    cancellationReason: status === 'Cancelled' ? 'Customer no longer needs the service' : undefined,
    complaintId: i % 7 === 0 ? `CMP-${5001 + Math.floor(i / 7)}` : undefined,
    reviewId: status === 'Completed' && i % 2 === 0 ? `REV-${6001 + i}` : undefined,
  };
});

export const mockJobs: ServiceRequest[] = mockRequests.filter((r) => r.status !== 'Pending');

export const mockPayments: Payment[] = Array.from({ length: 22 }, (_, i) => {
  const job = mockRequests[i % mockRequests.length];
  const provider = mockProviders[i % mockProviders.length];
  return {
    id: `TXN-${String(7001 + i)}`,
    jobId: job.id.replace('REQ', 'JOB'),
    customerId: job.customerId,
    customerName: job.customerName,
    providerId: provider.id,
    providerName: provider.name,
    amount: job.estimatedPrice,
    method: methods[i % methods.length],
    date: daysAgo(25 - i),
    status: payStatuses[i % payStatuses.length],
  };
});

const complaintStatuses: Complaint['status'][] = [
  'Open',
  'Under Review',
  'Resolved',
  'Rejected',
  'Closed',
];

export const mockComplaints: Complaint[] = Array.from({ length: 12 }, (_, i) => {
  const job = mockRequests[i * 2];
  const status = complaintStatuses[i % complaintStatuses.length];
  return {
    id: `CMP-${String(5001 + i)}`,
    jobId: job.id.replace('REQ', 'JOB'),
    submittedBy: i % 2 === 0 ? job.customerName : job.providerName || 'Provider',
    submittedByType: i % 2 === 0 ? 'Customer' : 'Provider',
    against: i % 2 === 0 ? job.providerName || 'Provider' : job.customerName,
    againstType: i % 2 === 0 ? 'Provider' : 'Customer',
    type: ['Service Quality', 'Punctuality', 'Payment Dispute', 'Behavior', 'No-Show'][i % 5],
    description: `Complaint regarding job ${job.id}: ${
      i % 2 === 0 ? 'Work was incomplete and not as described.' : 'Customer was unavailable at scheduled time.'
    }`,
    createdAt: daysAgo(20 - i),
    status,
    notes: status !== 'Open' ? [{ id: `NOTE-${i}`, text: 'Initial review started.', createdAt: daysAgo(19 - i) }] : [],
    resolutionReason: status === 'Resolved' ? 'Issue resolved with partial refund' : undefined,
    history: [
      { id: `CH-${i}-1`, label: 'Complaint Opened', date: daysAgo(20 - i) },
      ...(status !== 'Open'
        ? [{ id: `CH-${i}-2`, label: 'Under Review', date: daysAgo(19 - i) }]
        : []),
      ...(status === 'Resolved' || status === 'Closed'
        ? [{ id: `CH-${i}-3`, label: status, date: daysAgo(15 - i) }]
        : []),
    ],
  };
});

export const mockReviews: Review[] = Array.from({ length: 16 }, (_, i) => {
  const job = mockRequests.filter((r) => r.status === 'Completed')[i % 6] || mockRequests[0];
  const fromCustomer = i % 3 !== 0;
  return {
    id: `REV-${String(6001 + i)}`,
    jobId: job.id.replace('REQ', 'JOB'),
    reviewer: fromCustomer ? job.customerName : job.providerName || 'Provider',
    reviewerType: fromCustomer ? 'Customer' : 'Provider',
    reviewedParty: fromCustomer ? job.providerName || 'Provider' : job.customerName,
    reviewedPartyType: fromCustomer ? 'Provider' : 'Customer',
    rating: (i % 5) + 1,
    text: [
      'Excellent service, on time and professional.',
      'Good work overall, minor delays.',
      'Average experience, communication could improve.',
      'Not satisfied with the quality of work.',
      'Outstanding! Highly recommended.',
    ][i % 5],
    date: daysAgo(18 - i),
    status: i === 4 || i === 11 ? 'Hidden' : 'Active',
  };
});

export const mockNotifications: AppNotification[] = Array.from({ length: 10 }, (_, i) => {
  const target: AppNotification['target'] =
    i % 2 === 0 ? 'Specific User' : 'Specific Service Provider';
  const status: AppNotification['status'] = i === 7 ? 'Failed' : i === 8 ? 'Pending' : 'Sent';
  const recipientName =
    target === 'Specific User'
      ? mockCustomers[i % mockCustomers.length].name
      : mockProviders[i % mockProviders.length].name;
  return {
    id: `NTF-${String(8001 + i)}`,
    title: [
      'Platform Maintenance',
      'New Service Categories',
      'Payment Update',
      'Holiday Schedule',
      'Safety Guidelines',
    ][i % 5],
    message: 'Please review the latest updates from the HomeHero admin team.',
    target,
    recipientName,
    recipientCount: 1,
    createdAt: daysAgo(15 - i),
    status,
    recipients: [
      {
        id: `RCP-${i}-0`,
        name: recipientName,
        status,
      },
    ],
  };
});

export const mockCmsPages: CmsPage[] = [
  {
    id: 'CMS-1',
    title: 'About Us',
    slug: 'about-us',
    content:
      'HomeHero connects customers with trusted local handy persons for plumbing, electrical, cleaning, and more.',
    status: 'Published',
    updatedAt: daysAgo(12),
  },
  {
    id: 'CMS-2',
    title: 'Terms & Conditions',
    slug: 'terms',
    content: 'By using HomeHero you agree to our terms of service, booking policies, and community guidelines.',
    status: 'Published',
    updatedAt: daysAgo(40),
  },
  {
    id: 'CMS-3',
    title: 'Privacy Policy',
    slug: 'privacy',
    content: 'We collect account and booking information to operate the marketplace and improve service quality.',
    status: 'Published',
    updatedAt: daysAgo(35),
  },
];

export const mockFaqs: FaqItem[] = [
  {
    id: 'FAQ-1',
    question: 'How do I book a service?',
    answer: 'Select a category, describe your need, pick a time, and confirm the booking request.',
    status: 'Published',
    updatedAt: daysAgo(10),
  },
  {
    id: 'FAQ-2',
    question: 'How are providers verified?',
    answer: 'Providers submit ID, licenses, and insurance. Admins review documents before approval.',
    status: 'Published',
    updatedAt: daysAgo(9),
  },
  {
    id: 'FAQ-3',
    question: 'What if I need to cancel?',
    answer: 'You can cancel from the job details screen. Cancellation policies may apply.',
    status: 'Published',
    updatedAt: daysAgo(8),
  },
  {
    id: 'FAQ-4',
    question: 'How do refunds work?',
    answer: 'Eligible refunds are processed to the original payment method within 5–7 business days.',
    status: 'Draft',
    updatedAt: daysAgo(3),
  },
];

export const mockDashboardStats: DashboardStats = {
  users: { total: mockCustomers.length, active: mockCustomers.filter((u) => u.status === 'Active').length, suspended: mockCustomers.filter((u) => u.status === 'Suspended').length, change: 12.4 },
  providers: {
    total: mockProviders.length,
    pending: mockProviders.filter((p) => p.verificationStatus === 'Pending').length,
    approved: mockProviders.filter((p) => p.verificationStatus === 'Approved').length,
    suspended: mockProviders.filter((p) => p.status === 'Suspended').length,
    change: 8.1,
  },
  jobs: {
    total: mockJobs.length,
    active: mockJobs.filter((j) => ['Assigned', 'Accepted', 'In Progress'].includes(j.status)).length,
    completed: mockJobs.filter((j) => j.status === 'Completed').length,
    cancelled: mockJobs.filter((j) => j.status === 'Cancelled').length,
    change: 5.6,
  },
  payments: {
    successful: mockPayments.filter((p) => p.status === 'Successful').length,
    pending: mockPayments.filter((p) => p.status === 'Pending').length,
    failed: mockPayments.filter((p) => p.status === 'Failed').length,
    cancelled: mockPayments.filter((p) => p.status === 'Cancelled').length,
    refunded: mockPayments.filter((p) => p.status === 'Refunded').length,
    change: 3.2,
  },
};

export const chartRegistrations = [
  { month: 'Jan', users: 42 },
  { month: 'Feb', users: 55 },
  { month: 'Mar', users: 61 },
  { month: 'Apr', users: 48 },
  { month: 'May', users: 72 },
  { month: 'Jun', users: 88 },
  { month: 'Jul', users: 95 },
  { month: 'Aug', users: 110 },
];

export const chartJobsByStatus = [
  { name: 'Pending', value: mockRequests.filter((r) => r.status === 'Pending').length },
  { name: 'Active', value: mockRequests.filter((r) => ['Assigned', 'Accepted', 'In Progress'].includes(r.status)).length },
  { name: 'Completed', value: mockRequests.filter((r) => r.status === 'Completed').length },
  { name: 'Cancelled', value: mockRequests.filter((r) => r.status === 'Cancelled').length },
];

export const chartPayments = [
  { name: 'Successful', value: mockPayments.filter((p) => p.status === 'Successful').length },
  { name: 'Pending', value: mockPayments.filter((p) => p.status === 'Pending').length },
  { name: 'Failed', value: mockPayments.filter((p) => p.status === 'Failed').length },
  { name: 'Refunded', value: mockPayments.filter((p) => p.status === 'Refunded').length },
];

export const chartCategories = categories.slice(0, 6).map((name, i) => ({
  name,
  value: 20 + i * 8,
}));
